/**
 * お知らせアーカイブ: カテゴリは URL を変えず REST で一覧を差し替え（ページ全体の再読み込みなし）
 * 件数: PC 10 件 / ページ、SP（viewport max-width:767px）8 件 / ページ（enqueue の値と Tailwind `sp` に合わせる）
 */
class NewsArchive {
  constructor() {
    this.root = document.querySelector('.js-news-archive');
    if (!this.root || typeof sanokikoNewsArchive === 'undefined') {
      return;
    }

    this._reqId = 0;
    this._activeTermId = null;
    this._currentCategoryPage = 1;
    this._currentCategoryMaxPages = 1;

    this.pcPerPage = sanokikoNewsArchive.postsPerPagePc || 10;
    this.spPerPage = sanokikoNewsArchive.postsPerPageSp || 8;

    this.listEl = this.root.querySelector('.js-news-archive-list');
    this.anchorEl = document.getElementById('archive-anchor');
    this.pagingWrap = this.root.querySelector('.js-news-archive-paging');
    this.filters = this.root.querySelectorAll('.js-news-cat-filter');
    this.restUrl = sanokikoNewsArchive.restUrl;
    this.termNames = sanokikoNewsArchive.termNames || {};
    this.emptyMessage =
      sanokikoNewsArchive.emptyMessage || '該当するお知らせはありません。';

    if (!this.listEl || !this.filters.length) {
      return;
    }

    this.initialListHtml = this.listEl.innerHTML;
    this.initialPagingHtml = this.pagingWrap ? this.pagingWrap.innerHTML : '';

    this._loadingOverlay = this._createLoadingOverlay();
    this.root.style.position = 'relative';
    this.root.appendChild(this._loadingOverlay);

    this._bind();
    this._bindPagingDelegation();
    this._bindViewportChange();
  }

  _createLoadingOverlay() {
    const overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('role', 'status');
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      zIndex: '10',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'rgba(255,255,255,0.75)',
      opacity: '0',
      pointerEvents: 'none',
      transition: 'opacity 0.2s',
    });

    const spinner = document.createElement('span');
    Object.assign(spinner.style, {
      display: 'block',
      width: '4rem',
      height: '4rem',
      borderRadius: '50%',
      border: '.4rem solid #e0e0e0',
      borderTopColor: '#2A892C',
      animation: 'news-archive-spin 0.8s linear infinite',
    });
    overlay.appendChild(spinner);

    if (!document.getElementById('news-archive-spin-style')) {
      const style = document.createElement('style');
      style.id = 'news-archive-spin-style';
      style.textContent =
        '@keyframes news-archive-spin{to{transform:rotate(360deg)}}';
      document.head.appendChild(style);
    }

    return overlay;
  }

  _showLoading() {
    this._loadingOverlay.style.opacity = '1';
    this._loadingOverlay.style.pointerEvents = 'auto';
  }

  _hideLoading() {
    this._loadingOverlay.style.opacity = '0';
    this._loadingOverlay.style.pointerEvents = 'none';
  }

  _getPerPage() {
    return window.matchMedia('(max-width: 767px)').matches
      ? this.spPerPage
      : this.pcPerPage;
  }

  /**
   * カテゴリ絞り込み・ページ送り後、#archive-anchor までスクロール（無ければ一覧 ul）
   */
  _scrollToArchiveAnchor() {
    const el = this.anchorEl || this.listEl;
    if (!el) return;
    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    requestAnimationFrame(() => {
      el.scrollIntoView({
        behavior: prefersReduced ? 'auto' : 'smooth',
        block: 'start',
      });
    });
  }

  _bind() {
    this.filters.forEach((btn) => {
      btn.addEventListener('click', () => {
        const termId = btn.dataset.termId;
        this._activateFilter(btn);
        if (!termId) {
          this._showAll();
        } else {
          this._loadCategory(termId, 1);
        }
      });
    });
  }

  _bindPagingDelegation() {
    if (!this.pagingWrap) return;
    this.pagingWrap.addEventListener('click', (e) => {
      if (this._activeTermId == null) return;
      const pageBtn = e.target.closest('.js-news-archive-page');
      if (pageBtn) {
        const p = parseInt(pageBtn.dataset.page, 10);
        if (!Number.isNaN(p)) {
          this._loadCategory(this._activeTermId, p);
        }
        return;
      }
      if (e.target.closest('.js-news-archive-prev')) {
        if (this._currentCategoryPage > 1) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage - 1);
        }
        return;
      }
      if (e.target.closest('.js-news-archive-next')) {
        if (this._currentCategoryPage < this._currentCategoryMaxPages) {
          this._loadCategory(this._activeTermId, this._currentCategoryPage + 1);
        }
      }
    });
  }

  _bindViewportChange() {
    let prev = this._getPerPage();
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = () => {
      const next = this._getPerPage();
      if (next === prev) return;
      prev = next;
      if (this._activeTermId != null) {
        this._loadCategory(this._activeTermId, 1);
      }
    };
    if (mq.addEventListener) {
      mq.addEventListener('change', onChange);
    } else {
      mq.addListener(onChange);
    }
  }

  _activateFilter(activeBtn) {
    this.filters.forEach((b) => {
      const on = b === activeBtn;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
      if (on) {
        b.setAttribute('aria-current', 'true');
      } else {
        b.removeAttribute('aria-current');
      }
    });
  }

  _showAll() {
    this._activeTermId = null;
    this._reqId += 1;
    this._hideLoading();
    this.listEl.innerHTML = this.initialListHtml;
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = this.initialPagingHtml;
      this.pagingWrap.hidden = false;
    }
  }

  _clearPaging() {
    if (this.pagingWrap) {
      this.pagingWrap.innerHTML = '';
      this.pagingWrap.hidden = true;
    }
  }

  _setEmpty() {
    this.listEl.innerHTML = `<li class="text-center text-[1.4rem] text-[#888888] py-[2rem]">${this.emptyMessage}</li>`;
    this._clearPaging();
    this._scrollToArchiveAnchor();
  }

  async _loadCategory(termId, page = 1) {
    const req = ++this._reqId;
    this._activeTermId = termId;
    this._showLoading();

    let result;
    try {
      result = await this._fetchPostsPage(termId, page);
    } catch (e) {
      if (req !== this._reqId) return;
      this._hideLoading();
      this._setEmpty();
      return;
    }

    if (req !== this._reqId) return;
    this._hideLoading();

    const { posts, totalPages } = result;
    this._currentCategoryPage = page;
    this._currentCategoryMaxPages = Math.max(1, totalPages);

    if (!posts.length) {
      this._setEmpty();
      return;
    }

    this.listEl.innerHTML = posts.map((p) => this._renderPost(p)).join('');

    if (this.pagingWrap) {
      this.pagingWrap.hidden = false;
      this.pagingWrap.innerHTML = this._renderPagingNav(page, this._currentCategoryMaxPages);
    }

    this._scrollToArchiveAnchor();
  }

  async _fetchPostsPage(termId, page) {
    const perPage = this._getPerPage();
    const url = new URL(this.restUrl, window.location.origin);
    url.searchParams.set('per_page', String(perPage));
    url.searchParams.set('page', String(page));
    url.searchParams.set('news_cat', String(termId));

    const res = await fetch(url.toString(), {
      credentials: 'same-origin',
    });

    if (!res.ok) {
      throw new Error('REST request failed');
    }

    const totalPages = parseInt(res.headers.get('X-WP-TotalPages') || '0', 10);
    const posts = await res.json();
    return {
      posts: Array.isArray(posts) ? posts : [],
      totalPages: Number.isFinite(totalPages) ? totalPages : 0,
    };
  }

  _buildPageItems(maxPages, paged) {
    if (maxPages <= 1) return [];
    const show = { 1: true, [maxPages]: true };
    const left = Math.max(2, paged - 2);
    const right = Math.min(maxPages - 1, paged + 2);
    for (let i = left; i <= right; i++) show[i] = true;
    const keys = Object.keys(show)
      .map(Number)
      .sort((a, b) => a - b);
    const items = [];
    let prevKey = 0;
    for (const p of keys) {
      if (prevKey && p - prevKey > 1) {
        items.push({ ellipsis: true });
      }
      items.push({ page: p });
      prevKey = p;
    }
    return items;
  }

  _renderPagingNav(paged, maxPages) {
    if (maxPages <= 1) return '';

    const clsNav =
      'flex justify-center items-center gap-[.6rem] [&_*]:font-outfit';
    const clsPrevNextBase =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-bold text-[1.4rem] duration-300 pc:hover:opacity-70';
    const clsBtn =
      'js-news-archive-page w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-white text-[1.8rem] duration-300 [background:_radial-gradient(94.38%_94.38%_at_19.25%_6.75%,_#3DA73F_0%,_#2A892C_100%)] border border-[#CCCCCC] pc:hover:opacity-70';
    const clsCurrent =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] rounded-full flex items-center justify-center font-normal text-[1.8rem] duration-300 bg-white [border:_.1rem_solid_#2A892C] text-[#2A892C]';
    const clsEllipsis =
      'w-[3.6rem] h-[3.6rem] sp:w-[3rem] sp:h-[3rem] flex items-center justify-center text-[1.8rem] text-[#888888]';

    const svgPrev = `<span style="transform:scaleX(-1);display:flex;"><svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>`;
    const svgNext = `<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M1 1L6 6L1 11" stroke="#2A892C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>`;

    let prevBlock;
    if (paged <= 1) {
      prevBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgPrev}</span>`;
    } else {
      prevBlock = `<button type="button" class="${clsPrevNextBase} js-news-archive-prev bg-white text-[#2A892C]" aria-label="前のページへ">${svgPrev}</button>`;
    }

    let nextBlock;
    if (paged >= maxPages) {
      nextBlock = `<span class="${clsPrevNextBase} opacity-30 pointer-events-none" aria-hidden="true">${svgNext}</span>`;
    } else {
      nextBlock = `<button type="button" class="${clsPrevNextBase} js-news-archive-next bg-white text-[#2A892C]" aria-label="次のページへ">${svgNext}</button>`;
    }

    const pageItems = this._buildPageItems(maxPages, paged);
    const middle = pageItems
      .map((item) => {
        if (item.ellipsis) {
          return `<span class="${clsEllipsis}" aria-hidden="true">…</span>`;
        }
        const num = item.page;
        if (num === paged) {
          return `<span class="${clsCurrent}" aria-current="page">${num}</span>`;
        }
        return `<button type="button" class="${clsBtn}" data-page="${num}">${num}</button>`;
      })
      .join('');

    return `<nav class="${clsNav}" aria-label="お知らせのページ送り">${prevBlock}${middle}${nextBlock}</nav>`;
  }

  _formatDate(iso) {
    const d = new Date(iso);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  }

  _decodeTitle(html) {
    const ta = document.createElement('textarea');
    ta.innerHTML = html;
    return ta.value;
  }

  _escapeHtml(str) {
    const d = document.createElement('div');
    d.textContent = str;
    return d.innerHTML;
  }

  _safeHref(link) {
    return /^https?:\/\//i.test(link || '') ? link : '#';
  }

  _renderPost(post) {
    const titleRaw = post.title && post.title.rendered ? post.title.rendered : '';
    const title = this._escapeHtml(this._decodeTitle(titleRaw));
    const date = this._formatDate(post.date);
    const catIds = post.news_cat || [];
    const catName =
      catIds.length > 0
        ? this._escapeHtml(this.termNames[String(catIds[0])] || '')
        : '';
    const href = this._safeHref(post.link);

    return `<li>
<a href="${href}" class="
  bg-[#F4F4F4]
  py-[1.9rem]
  pl-[2rem]
  pr-[3rem]
  rounded-[.4rem]
  relative
  block

  after:absolute
  after:top-1/2
  after:-translate-y-1/2
  after:right-[2rem]
  after:w-[.6rem]
  after:h-[1.1rem]
  after:[background:_url('../images/common/ic-arrow-right.svg')_no-repeat_center_center_/_contain]

  sp:after:right-[1.5rem]

  pc:[&_p]:hover:text-[#2A892C]
">
  <div class="
    flex
    gap-[1rem]
  ">
    <p class="
      shrink-0
      text-[1.3rem]
      text-[#888888]
      font-outfit

      pc:duration-[.3s]
    ">${date}</p>
    <p class="
      shrink-0
      text-[1.4rem]
      bg-white
      text-[#2A892C]
      px-[1.1rem]
      rounded-[9999px]
      leading-[1.5]
      [&:empty]:hidden
    ">${catName}</p>
  </div>
  <p class="
    line-clamp-1
    mt-[0.5rem]

    pc:duration-[.3s]
  ">${title}</p>
</a>
</li>`;
  }
}
